import { buildWebSocketUrl, connectReconnectingSocket, type LiveConnectionState } from './reconnectingSocket';

interface SharedFolderConnectedMessage {
  type: 'sharedFolder.connected';
}

interface SharedFolderManifestRevisionMessage {
  type: 'sharedFolder.manifestRevision';
  folderId: string;
  revision: number;
}

interface SharedFolderAccessEndedMessage {
  type: 'sharedFolder.accessEnded';
  folderId: string;
  inviteId?: string | null;
  reason: string;
}

type SharedFolderLiveMessage =
  | SharedFolderConnectedMessage
  | SharedFolderManifestRevisionMessage
  | SharedFolderAccessEndedMessage;

export interface SharedFolderLiveCallbacks {
  onManifestRevision?: (revision: number) => void;
  onAccessEnded?: (info: { inviteId: string | null; reason: string }) => void;
  onStateChange?: (state: LiveConnectionState) => void;
}

let activeSocket: ReturnType<typeof connectReconnectingSocket> | null = null;
let callbacks: SharedFolderLiveCallbacks | null = null;
let activeFolderId: string | null = null;

function handleMessage(message: SharedFolderLiveMessage): void {
  if (message.type === 'sharedFolder.manifestRevision'
    && activeFolderId
    && message.folderId === activeFolderId) {
    callbacks?.onManifestRevision?.(message.revision);
    return;
  }

  if (message.type === 'sharedFolder.accessEnded'
    && activeFolderId
    && message.folderId === activeFolderId) {
    callbacks?.onAccessEnded?.({
      inviteId: message.inviteId ?? null,
      reason: message.reason,
    });
  }
}

export function connectSharedFolderLive(
  folderId: string,
  options: SharedFolderLiveCallbacks,
): void {
  disconnectSharedFolderLive();
  activeFolderId = folderId;
  callbacks = options;

  const url = `${buildWebSocketUrl('/api/ws/shared-folders')}?folderId=${encodeURIComponent(folderId)}`;
  activeSocket = connectReconnectingSocket({
    url,
    onMessage: (payload) => handleMessage(payload as SharedFolderLiveMessage),
    onStateChange: (state) => callbacks?.onStateChange?.(state),
  });
}

export function disconnectSharedFolderLive(): void {
  activeSocket?.close();
  activeSocket = null;
  callbacks = null;
  activeFolderId = null;
}
